import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Typography,
  Button,
  Input,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Alert,
  Spinner,
  Select,
  Option,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
} from "@material-tailwind/react";
import {
  UserPlusIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  CameraIcon,
  TrashIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { collection, doc, setDoc, getDocs } from "firebase/firestore";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import heic2any from "heic2any";
import db, { storage } from "../../firebase";

const functions = getFunctions();

if (window.location.href.indexOf("localhost") > -1) {
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
}

// Available teams
const AVAILABLE_TEAMS = [
  { id: "team_a", name: "Team A" },
  { id: "team_b", name: "Team B" },
];

// Permission claims shown as toggleable chips on each user row
const PERMISSIONS = [
  { claim: "LMAData", label: "LMA Data" },
  { claim: "reportAnalytics", label: "Reports" },
  { claim: "canDelegate", label: "Delegates" },
  { claim: "compsEditor", label: "Comps" },
  { claim: "valuationAdmin", label: "Valuation" },
  { claim: "webStatsAdmin", label: "Web Stats" },
  { claim: "cirrusNetwork", label: "Cirrus Network" },
  { claim: "estSalesReport", label: "Est Sales" },
];

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [userSearch, setUserSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states for create user
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserFullName, setNewUserFullName] = useState("");
  const [newUserPhone, setNewUserPhone] = useState("");
  const [newUserTitle, setNewUserTitle] = useState("");
  const [newUserTeam, setNewUserTeam] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Form states for edit user
  const [editFullName, setEditFullName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editTeam, setEditTeam] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  // Photo upload states
  const [editPhotoURL, setEditPhotoURL] = useState("");
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const editPhotoInputRef = useRef(null);

  // Delegate states
  const [editDelegates, setEditDelegates] = useState([]);
  const [delegateSearch, setDelegateSearch] = useState("");

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get users from Firebase Auth via cloud function
      const getUserStatus = httpsCallable(functions, "getUserStatus");
      const result = await getUserStatus();
      setUsers(result.data || []);

      // Get user profiles from user_data collection
      const userDataSnapshot = await getDocs(collection(db, "user_data"));
      const profiles = {};
      userDataSnapshot.forEach((doc) => {
        profiles[doc.id] = doc.data();
      });
      setUserProfiles(profiles);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to fetch users. Make sure you have admin or userManage permissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle claim toggle
  const handleClaimToggle = async (email, claimName, currentValue) => {
    try {
      setError(null);
      const setCustomClaims = httpsCallable(functions, "setCustomClaims");
      await setCustomClaims({
        email,
        claimName,
        enabled: !currentValue,
      });
      setSuccess(`Updated ${claimName} for ${email}`);
      setTimeout(() => setSuccess(null), 3000);
      // Refresh user list
      await fetchUsers();
    } catch (err) {
      console.error("Error updating claim:", err);
      setError(`Failed to update ${claimName} for ${email}`);
    }
  };

  // Handle create user
  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserFullName || !newUserPhone || !newUserTitle || !newUserTeam) {
      setCreateError("All fields are required");
      return;
    }

    try {
      setIsCreating(true);
      setCreateError(null);

      // Create user via cloud function
      const createUserFn = httpsCallable(functions, "createUser");
      const result = await createUserFn({
        email: newUserEmail,
        displayName: newUserFullName,
      });

      if (result.data?.uid) {
        // Save additional profile data to user_data collection
        await setDoc(doc(db, "user_data", result.data.uid), {
          displayName: newUserFullName,
          phoneNumber: newUserPhone,
          title: newUserTitle,
          teamId: newUserTeam,
          createdAt: new Date().toISOString(),
        });

        // Set default claims: LMAData off, reportAnalytics on
        const setCustomClaims = httpsCallable(functions, "setCustomClaims");
        await setCustomClaims({
          email: newUserEmail,
          claimName: "reportAnalytics",
          enabled: true,
        });

        // Send password reset email so user can set their own password
        const auth = getAuth();
        await sendPasswordResetEmail(auth, newUserEmail);
      }

      setSuccess(`User ${newUserEmail} created. A password reset email has been sent.`);
      setCreateDialogOpen(false);
      resetCreateForm();
      await fetchUsers();
    } catch (err) {
      console.error("Error creating user:", err);
      setCreateError(err.message || "Failed to create user");
    } finally {
      setIsCreating(false);
    }
  };

  // Handle edit user
  const handleEditUser = async () => {
    if (!selectedUser) return;

    if (!editFullName || !editPhone || !editTitle || !editTeam) {
      setEditError("All fields are required");
      return;
    }

    try {
      setIsSaving(true);
      setEditError(null);

      // Update Firebase Auth displayName
      const updateUserProfileFn = httpsCallable(functions, "updateUserProfile");
      await updateUserProfileFn({ uid: selectedUser.userId, displayName: editFullName });

      // Update user_data collection
      await setDoc(
        doc(db, "user_data", selectedUser.userId),
        {
          phoneNumber: editPhone,
          title: editTitle,
          teamId: editTeam,
          displayName: editFullName,
          email: selectedUser.email,
          delegates: editDelegates,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      // Sync delegateFor custom claim on affected assistants
      const previousDelegates = (userProfiles[selectedUser.userId] || {}).delegates || [];
      const setCustomClaimsFn = httpsCallable(functions, "setCustomClaims");

      // Find assistants whose delegateFor needs updating
      const removedDelegates = previousDelegates.filter(
        (prev) => !editDelegates.some((cur) => cur.uid === prev.uid)
      );
      const addedDelegates = editDelegates.filter(
        (cur) => !previousDelegates.some((prev) => prev.uid === cur.uid)
      );
      const affectedAssistants = new Set([
        ...removedDelegates.map((d) => d.uid),
        ...addedDelegates.map((d) => d.uid),
      ]);

      // For each affected assistant, rebuild their delegateFor from all agents' delegates
      for (const assistantUid of affectedAssistants) {
        const assistantEmail = [...removedDelegates, ...addedDelegates].find((d) => d.uid === assistantUid)?.email;
        if (!assistantEmail) continue;

        // Build the full delegateFor array by checking all user profiles
        const delegateForUids = [];
        // Check current agent being edited
        if (editDelegates.some((d) => d.uid === assistantUid)) {
          delegateForUids.push(selectedUser.userId);
        }
        // Check all other agents' profiles for this assistant
        for (const [agentUid, profile] of Object.entries(userProfiles)) {
          if (agentUid === selectedUser.userId) continue;
          if ((profile.delegates || []).some((d) => d.uid === assistantUid)) {
            delegateForUids.push(agentUid);
          }
        }

        await setCustomClaimsFn({
          email: assistantEmail,
          claimName: "delegateFor",
          enabled: delegateForUids,
        });
      }

      setSuccess(`Updated profile for ${selectedUser.email}`);
      setEditDialogOpen(false);
      setEditError(null);
      await fetchUsers();
    } catch (err) {
      console.error("Error updating user:", err);
      setEditError("Failed to update user profile");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle disable user
  const handleDisableUser = async (user) => {
    try {
      setError(null);
      const disableUserFn = httpsCallable(functions, "disableUser");
      await disableUserFn({ uid: user.userId });
      setSuccess(`User ${user.email} has been disabled`);
      setTimeout(() => setSuccess(null), 3000);
      await fetchUsers();
    } catch (err) {
      console.error("Error disabling user:", err);
      setError("Failed to disable user");
    }
  };

  // Handle password reset
  const handleResetPassword = async (user) => {
    try {
      setError(null);
      const auth = getAuth();
      await sendPasswordResetEmail(auth, user.email);
      setSuccess(`Password reset email sent to ${user.email}`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error("Error sending password reset:", err);
      setError(`Failed to send password reset email to ${user.email}`);
    }
  };

  // Handle enable user
  const handleEnableUser = async (user) => {
    try {
      setError(null);
      const enableUserFn = httpsCallable(functions, "enableUser");
      await enableUserFn({ uid: user.userId });
      setSuccess(`User ${user.email} has been enabled`);
      setTimeout(() => setSuccess(null), 3000);
      await fetchUsers();
    } catch (err) {
      console.error("Error enabling user:", err);
      setError("Failed to enable user");
    }
  };

  // Handle photo upload for selected user
  const handlePhotoUpload = async (event) => {
    let file = event.target.files?.[0];
    if (!file || !selectedUser) return;

    // Check if file is HEIC format
    const isHeic = file.type === "image/heic" ||
                   file.type === "image/heif" ||
                   file.name.toLowerCase().endsWith(".heic") ||
                   file.name.toLowerCase().endsWith(".heif");

    // Validate file type
    if (!file.type.startsWith("image/") && !isHeic) {
      setEditError("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setEditError("Image must be less than 5MB");
      return;
    }

    try {
      setIsUploadingPhoto(true);
      setEditError(null);

      // Convert HEIC to JPEG if needed
      if (isHeic) {
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.9,
          });
          file = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        } catch (conversionError) {
          console.error("HEIC conversion error:", conversionError);
          setEditError("Unable to convert HEIC image. Please use a JPEG or PNG file.");
          setIsUploadingPhoto(false);
          if (editPhotoInputRef.current) {
            editPhotoInputRef.current.value = "";
          }
          return;
        }
      }

      // Upload to Firebase Storage under the selected user's path
      const storageRef = ref(storage, `profile_photos/${selectedUser.userId}`);
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Save to user_data collection (cannot call updateProfile for other users)
      await setDoc(
        doc(db, "user_data", selectedUser.userId),
        {
          profilePhotoUrl: downloadURL,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setEditPhotoURL(downloadURL);

      // Update local userProfiles state so table reflects the change
      setUserProfiles((prev) => ({
        ...prev,
        [selectedUser.userId]: {
          ...prev[selectedUser.userId],
          profilePhotoUrl: downloadURL,
        },
      }));
    } catch (err) {
      console.error("Error uploading photo:", err);
      setEditError("Failed to upload photo. Please try again.");
    } finally {
      setIsUploadingPhoto(false);
      if (editPhotoInputRef.current) {
        editPhotoInputRef.current.value = "";
      }
    }
  };

  // Get initials from a name or email
  const getInitials = (name, email) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return "U";
  };

  // Open edit dialog
  const openEditDialog = (user) => {
    setSelectedUser(user);
    const profile = userProfiles[user.userId] || {};
    setEditFullName(profile.displayName || user.displayName || "");
    setEditPhone(profile.phoneNumber || "");
    setEditTitle(profile.title || "");
    setEditTeam(profile.teamId || "");
    setEditPhotoURL(profile.profilePhotoUrl || "");
    setEditDelegates(profile.delegates || []);
    setDelegateSearch("");
    setEditDialogOpen(true);
  };

  // Reset create form
  const resetCreateForm = () => {
    setNewUserEmail("");
    setNewUserFullName("");
    setNewUserPhone("");
    setNewUserTitle("");
    setNewUserTeam("");
    setCreateError(null);
  };

  // Get team name by ID (teams not displayed yet; kept for the edit dialog)
  // eslint-disable-next-line no-unused-vars
  const getTeamName = (teamId) => {
    const team = AVAILABLE_TEAMS.find((t) => t.id === teamId);
    return team?.name || "-";
  };

  // Filter users by search text (email, name, phone, title, team) and status
  const searchQ = userSearch.trim().toLowerCase();
  const matchesSearch = (u) => {
    if (!searchQ) return true;
    const profile = userProfiles[u.userId] || {};
    return [
      u.email,
      u.displayName,
      profile.displayName,
      profile.phoneNumber,
      profile.title,
    ].some((v) => (v || "").toLowerCase().includes(searchQ));
  };
  const activeUsers = users.filter((u) => !u.disabled && matchesSearch(u));
  const disabledUsers = users.filter((u) => u.disabled && matchesSearch(u));

  // Render user table (scrolls within a fixed height; header stays pinned)
  const renderUserTable = (userList) => (
    <div className="max-h-[65vh] overflow-auto">
    <table className="w-full table-auto text-left">
      <thead>
        <tr>
          {["User", "Details", "Permissions", "Actions"].map(
            (header) => (
              <th
                key={header}
                className="sticky top-0 z-10 border-b border-blue-gray-50 bg-white py-3 px-5 text-left"
              >
                <Typography
                  variant="small"
                  className="text-[11px] font-bold uppercase text-blue-gray-400"
                >
                  {header}
                </Typography>
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody>
        {userList.length === 0 ? (
          <tr>
            <td colSpan={4} className="py-8 text-center">
              <Typography variant="small" className="text-blue-gray-400">
                No users found
              </Typography>
            </td>
          </tr>
        ) : (
          userList.map((user, index) => {
            const profile = userProfiles[user.userId] || {};
            const className = `py-3 px-5 ${
              index === userList.length - 1 ? "" : "border-b border-blue-gray-50"
            }`;

            return (
              <tr key={user.userId}>
                <td className={`${className} align-top`}>
                  <div className="flex items-center gap-3">
                    {profile.profilePhotoUrl ? (
                      <img
                        src={profile.profilePhotoUrl}
                        alt=""
                        className="h-9 w-9 object-cover flex-shrink-0"
                        style={{ borderRadius: '50%' }}
                      />
                    ) : (
                      <div className="h-9 w-9 bg-blue-gray-100 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '50%' }}>
                        <span className="text-xs font-bold text-blue-gray-500">
                          {getInitials(profile.displayName || user.displayName, user.email)}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <Typography variant="small" className="font-medium text-blue-gray-800">
                        {profile.displayName || user.displayName || "-"}
                      </Typography>
                      <Typography variant="small" className="truncate text-blue-gray-500">
                        {user.email}
                      </Typography>
                    </div>
                  </div>
                </td>
                <td className={`${className} align-top`}>
                  <Typography variant="small" className="text-blue-gray-700">
                    {profile.title || "-"}
                  </Typography>
                  {profile.phoneNumber && (
                    <Typography variant="small" className="text-blue-gray-500">
                      {profile.phoneNumber}
                    </Typography>
                  )}
                </td>
                <td className={`${className} align-top`}>
                  <div className="flex max-w-xl flex-wrap gap-1.5">
                    {PERMISSIONS.map((p) => {
                      const on = !!user[p.claim];
                      return (
                        <button
                          key={p.claim}
                          type="button"
                          title={`${p.label}: ${on ? "enabled" : "disabled"} — click to ${on ? "revoke" : "grant"}`}
                          onClick={() => handleClaimToggle(user.email, p.claim, on)}
                          className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                            on
                              ? "border-amber-400 bg-amber-50 text-amber-900"
                              : "border-blue-gray-100 bg-white text-blue-gray-500 hover:border-blue-gray-300 hover:text-blue-gray-700"
                          }`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-amber-500" : "bg-blue-gray-200"}`} />
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className={className}>
                  <div className="flex items-center gap-0">
                    <Button
                      size="sm"
                      variant="text"
                      className="p-2"
                      onClick={() => openEditDialog(user)}
                      title="Edit user"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="text"
                      color="blue"
                      className="p-2"
                      onClick={() => handleResetPassword(user)}
                      title="Send password reset email"
                    >
                      <KeyIcon className="h-4 w-4" />
                    </Button>
                    {user.disabled ? (
                      <Button
                        size="sm"
                        variant="text"
                        color="green"
                        className="p-2"
                        onClick={() => handleEnableUser(user)}
                        title="Enable user"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="text"
                        color="orange"
                        className="p-2"
                        onClick={() => handleDisableUser(user)}
                        title="Disable user"
                      >
                        <NoSymbolIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-12 w-12" />
      </div>
    );
  }

  return (
    <div className="mt-12 mb-8 flex flex-col gap-6">
      <Card>
        <CardHeader
          variant="gradient"
          className="mb-4 p-6 bg-lma-gold"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-normal tracking-widest uppercase text-white">
              User Management
            </h2>
            <Button
              size="sm"
              color="white"
              className="flex items-center gap-2"
              onClick={() => setCreateDialogOpen(true)}
            >
              <UserPlusIcon className="h-4 w-4" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardBody className="overflow-x-auto px-0 pt-0 pb-2">
          {error && (
            <Alert color="red" className="mx-4 mb-4" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert color="green" className="mx-4 mb-4" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <div className="mt-4 mb-4 px-4">
            <label className="flex w-full max-w-sm items-center gap-2 rounded-lg border border-blue-gray-100 bg-white px-3 py-2">
              <MagnifyingGlassIcon className="h-4 w-4 flex-none text-blue-gray-500" />
              <input
                className="w-full bg-transparent text-sm text-blue-gray-800 placeholder-blue-gray-400 focus:outline-none"
                placeholder="Search by name, email or title"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </label>
          </div>

          <Tabs value="active" className="px-4">
            <TabsHeader>
              <Tab value="active">
                Active Users ({activeUsers.length})
              </Tab>
              <Tab value="disabled">
                Disabled Users ({disabledUsers.length})
              </Tab>
            </TabsHeader>
            <TabsBody>
              <TabPanel value="active" className="px-0">
                {renderUserTable(activeUsers)}
              </TabPanel>
              <TabPanel value="disabled" className="px-0">
                {renderUserTable(disabledUsers)}
              </TabPanel>
            </TabsBody>
          </Tabs>
        </CardBody>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} handler={setCreateDialogOpen} size="md">
        <DialogHeader>Create New User</DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          {createError && (
            <Alert color="red" onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}
          <Input
            label="Email *"
            type="email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
          />
          <Input
            label="Full Name *"
            value={newUserFullName}
            onChange={(e) => setNewUserFullName(e.target.value)}
          />
          <Input
            label="Phone Number *"
            type="tel"
            value={newUserPhone}
            onChange={(e) => setNewUserPhone(e.target.value)}
          />
          <Input
            label="Job Title *"
            value={newUserTitle}
            onChange={(e) => setNewUserTitle(e.target.value)}
          />
          <Select
            label="Team *"
            value={newUserTeam}
            onChange={(val) => setNewUserTeam(val)}
          >
            {AVAILABLE_TEAMS.map((team) => (
              <Option key={team.id} value={team.id}>
                {team.name}
              </Option>
            ))}
          </Select>
          <Typography variant="small" className="text-blue-gray-500">
            The user will receive an email to set their password. Default permissions: Report Analytics enabled.
          </Typography>
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button
            variant="text"
            onClick={() => {
              setCreateDialogOpen(false);
              resetCreateForm();
            }}
          >
            Cancel
          </Button>
          <Button
            className="bg-lma-gold"
            onClick={handleCreateUser}
            disabled={isCreating}
          >
            {isCreating ? <Spinner className="h-4 w-4" /> : "Create User"}
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} handler={setEditDialogOpen} size="md">
        <DialogHeader>Edit User Profile</DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          {editError && (
            <Alert color="red" onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}

          {/* Profile Photo */}
          <input
            type="file"
            ref={editPhotoInputRef}
            onChange={handlePhotoUpload}
            accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
            className="hidden"
          />
          <div className="flex flex-col items-center mb-2">
            <div className="relative">
              {editPhotoURL ? (
                <img
                  src={editPhotoURL}
                  alt="Profile"
                  className="h-24 w-24 object-cover"
                  style={{ borderRadius: '50%' }}
                />
              ) : (
                <div className="h-24 w-24 bg-blue-gray-100 flex items-center justify-center" style={{ borderRadius: '50%' }}>
                  <span className="text-2xl font-bold text-blue-gray-500">
                    {getInitials(editFullName || selectedUser?.displayName, selectedUser?.email)}
                  </span>
                </div>
              )}
              <button
                onClick={() => editPhotoInputRef.current?.click()}
                disabled={isUploadingPhoto}
                className="absolute inset-0 h-24 w-24 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity disabled:opacity-100 cursor-pointer"
                style={{ borderRadius: '50%' }}
              >
                {isUploadingPhoto ? (
                  <Spinner className="h-6 w-6 text-white" />
                ) : (
                  <CameraIcon className="h-6 w-6 text-white" />
                )}
              </button>
            </div>
            <Typography variant="small" className="text-blue-gray-400 mt-2">
              Click photo to upload
            </Typography>
          </div>

          <Typography variant="small" className="text-blue-gray-500 -mb-2">
            Email: {selectedUser?.email}
          </Typography>
          <Input
            label="Full Name *"
            value={editFullName}
            onChange={(e) => setEditFullName(e.target.value)}
          />
          <Input
            label="Phone Number *"
            type="tel"
            value={editPhone}
            onChange={(e) => setEditPhone(e.target.value)}
          />
          <Input
            label="Job Title *"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
          />
          <Select
            label="Team *"
            value={editTeam}
            onChange={(val) => setEditTeam(val)}
          >
            {AVAILABLE_TEAMS.map((team) => (
              <Option key={team.id} value={team.id}>
                {team.name}
              </Option>
            ))}
          </Select>

          {/* Delegates */}
          {selectedUser?.canDelegate && (
            <div className="mt-2">
              <Typography variant="small" className="font-medium text-blue-gray-700 mb-2">
                Delegates (Assistants)
              </Typography>
              <div className="relative mb-2">
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={delegateSearch}
                  onChange={(e) => setDelegateSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
                {delegateSearch.trim() && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {users
                      .filter(
                        (u) =>
                          u.userId !== selectedUser?.userId &&
                          !u.disabled &&
                          !editDelegates.some((d) => d.uid === u.userId) &&
                          (u.email.toLowerCase().includes(delegateSearch.toLowerCase()) ||
                            (u.displayName || "").toLowerCase().includes(delegateSearch.toLowerCase()))
                      )
                      .map((u) => (
                        <button
                          key={u.userId}
                          onClick={() => {
                            setEditDelegates([
                              ...editDelegates,
                              {
                                uid: u.userId,
                                email: u.email,
                                name: u.displayName || "",
                                addedAt: new Date().toISOString(),
                              },
                            ]);
                            setDelegateSearch("");
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                        >
                          <div>
                            <p className="text-sm font-medium">{u.displayName || "No name"}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                          <span className="text-xs text-blue-500 font-medium">Add</span>
                        </button>
                      ))}
                    {users.filter(
                      (u) =>
                        u.userId !== selectedUser?.userId &&
                        !u.disabled &&
                        !editDelegates.some((d) => d.uid === u.userId) &&
                        (u.email.toLowerCase().includes(delegateSearch.toLowerCase()) ||
                          (u.displayName || "").toLowerCase().includes(delegateSearch.toLowerCase()))
                    ).length === 0 && (
                      <p className="text-sm text-gray-500 text-center p-2">No matching users</p>
                    )}
                  </div>
                )}
              </div>
              {editDelegates.length > 0 ? (
                <div className="space-y-1">
                  {editDelegates.map((d) => (
                    <div
                      key={d.uid}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="text-sm font-medium">{d.name || "No name"}</p>
                        <p className="text-xs text-gray-500">{d.email}</p>
                      </div>
                      <Button
                        variant="text"
                        size="sm"
                        color="red"
                        className="p-1"
                        onClick={() => setEditDelegates(editDelegates.filter((x) => x.uid !== d.uid))}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-2">No delegates assigned</p>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button variant="text" onClick={() => { setEditDialogOpen(false); setEditError(null); }}>
            Cancel
          </Button>
          <Button
            className="bg-lma-gold"
            onClick={handleEditUser}
            disabled={isSaving || isUploadingPhoto}
          >
            {isSaving ? <Spinner className="h-4 w-4" /> : "Save Changes"}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
